/*
 * Website redesign - product page behaviour, loaded by sections/main-product.liquid.
 * Plain script, no dependency, safe to load twice (the quick add popup re-injects section scripts).
 *
 * 1. Desktop image strip: auto-scrolling, draggable loop with pause / enlarge buttons
 *    (media-gallery[data-rd-marquee], section setting "Scroll the images automatically"); without
 *    auto-scrolling the strip can still be dragged with a mouse.
 * 2. Side drawers of the product details rows (<dialog class="details-drawer">) and accordion rows.
 * 3. Size guide drawer (theme <modal-v2 class="sizing-chart">): Esc, focus in / back / kept inside,
 *    "select a size" row and add to cart button.
 * 4. Size picking: no size preselected (variant-selects[data-size-pending]); without a size the add
 *    to cart buttons open the size sheet (<dialog data-size-sheet>) on phones and show a "choose a
 *    size" message on larger screens, where the size row is on the page.
 * 5. Phones: floating add to cart bar.
 * 6. Image viewer: the extra image under the gallery opens it; thumbnail rail.
 * 7. Gallery dots: aria-current on the active dot, labels counting the slides shown.
 */
(function () {
  'use strict';
  if (window.__shopifyPdp) return;
  window.__shopifyPdp = true;

  var desktop = window.matchMedia('(min-width: 990px)');
  var phone = window.matchMedia('(max-width: 749px)');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  function isShown(el) {
    return !!el && el.getClientRects().length > 0;
  }

  function setCurrent(el, current) {
    if (current) el.setAttribute('aria-current', 'true');
    else el.removeAttribute('aria-current');
  }

  function mainRoot() {
    return document.querySelector('section[id^="MainProduct-"] product-info');
  }

  function scope(el) {
    return el.closest('product-info') || (el.closest('quick-add-modal') && el.closest('quick-add-modal').querySelector('product-info')) || mainRoot() || document;
  }

  /* ------------------------------------------------------------------ 1. strip */
  // The slides are repeated after themselves and the list runs a linear CSS animation over one set
  // (keyframes rd-marquee in assets/redesign.css), so the browser animates it off the main thread.
  // Dragging / prev-next freeze it and restart it from there (negative animation-delay). Every photo
  // of the loop loads at once (no lazy loading). With reduced motion it starts paused ("play" runs it).
  var SPEED = 100; // px per second

  function Marquee(gallery) {
    this.gallery = gallery;
    this.viewer = gallery.querySelector('slider-component');
    this.list = gallery.querySelector('.product__media-list');
    this.controls = gallery.querySelector('.rd-marquee-controls');
    this.toggle = this.controls && this.controls.querySelector('.rd-marquee-controls__toggle');
    this.pos = 0;
    this.setWidth = 0;
    this.on = false;
    this.playing = !reduceMotion.matches;
    this.dragging = false;
    this.moved = false;
    this.bind();
    this.update();
  }

  Marquee.prototype.originals = function () {
    return Array.prototype.filter.call(this.list.children, function (li) {
      return li.classList.contains('product__media-item') && !li.hasAttribute('data-rd-clone') && !li.classList.contains('hidden');
    });
  };

  Marquee.prototype.update = function () {
    if (desktop.matches && this.list) this.build();
    else this.destroy();
  };

  Marquee.prototype.build = function () {
    var keep = this.on ? this.offset() : this.pos;
    this.removeClones();
    var items = this.originals();
    if (items.length < 2) {
      this.destroy();
      return;
    }
    // every photo of the loop is needed: no lazy loading (the copies inherit it); the first photo
    // keeps its own eager / high priority loading
    items.forEach(function (li) {
      li.querySelectorAll('img').forEach(function (img) {
        if (img.loading !== 'lazy') return;
        img.loading = 'eager';
        img.decoding = 'async';
      });
    });
    this.gallery.classList.add('rd-marquee--on');
    this.list.style.animation = 'none';
    this.list.style.transform = '';
    this.itemWidths = items.map(function (li) { return li.getBoundingClientRect().width; });
    var width = this.itemWidths.reduce(function (sum, w) { return sum + w; }, 0);
    if (!width) return;
    this.setWidth = width;
    this.items = items;
    var copies = Math.ceil((this.viewer.clientWidth || window.innerWidth) / width) + 1;
    var fragment = document.createDocumentFragment();
    for (var c = 0; c < copies; c++) {
      items.forEach(function (li) {
        var clone = li.cloneNode(true);
        clone.setAttribute('data-rd-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        clone.classList.remove('is-active');
        clone.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
        clone.querySelectorAll('a, button, input, video, iframe, [tabindex]').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
        fragment.appendChild(clone);
      });
    }
    this.list.appendChild(fragment);
    this.list.style.setProperty('--rd-loop', width + 'px');
    this.on = true;
    this.enableNav();
    this.run(keep);
  };

  Marquee.prototype.removeClones = function () {
    this.list.querySelectorAll(':scope > [data-rd-clone]').forEach(function (li) { li.remove(); });
  };

  Marquee.prototype.destroy = function () {
    this.on = false;
    if (this.tweenFrame) window.cancelAnimationFrame(this.tweenFrame);
    if (!this.list) return;
    this.removeClones();
    this.list.style.animation = '';
    this.list.style.transform = '';
    this.gallery.classList.remove('rd-marquee--on');
  };

  Marquee.prototype.normalize = function (value) {
    var w = this.setWidth || 1;
    return ((value % w) + w) % w;
  };

  // current offset of the running animation (or of the frozen strip)
  Marquee.prototype.offset = function () {
    var t = window.getComputedStyle(this.list).transform;
    if (!t || t === 'none') return this.normalize(this.pos);
    var m = t.match(/matrix(3d)?\(([^)]+)\)/);
    if (!m) return this.normalize(this.pos);
    var v = m[2].split(',').map(Number);
    return this.normalize(-(m[1] ? v[12] : v[4]));
  };

  // freeze the strip where it is (inline transform)
  Marquee.prototype.hold = function () {
    this.pos = this.offset();
    this.list.style.animation = 'none';
    this.render();
  };

  Marquee.prototype.render = function () {
    this.pos = this.normalize(this.pos);
    this.list.style.transform = 'translate3d(' + (-this.pos).toFixed(2) + 'px, 0, 0)';
  };

  // (re)start the loop from an offset (paused: it stays there; reduced motion starts paused)
  Marquee.prototype.run = function (offset) {
    this.pos = this.normalize(offset || 0);
    var list = this.list;
    list.style.transform = '';
    list.style.animation = 'none';
    void list.offsetWidth;
    list.style.animation = 'rd-marquee ' + (this.setWidth / SPEED).toFixed(3) + 's linear ' + (-this.pos / SPEED).toFixed(3) + 's infinite';
    list.style.animationPlayState = this.playing ? 'running' : 'paused';
  };

  Marquee.prototype.setPlaying = function (playing) {
    this.playing = playing;
    if (this.list && this.on) this.list.style.animationPlayState = playing ? 'running' : 'paused';
    if (!this.toggle) return;
    this.toggle.classList.toggle('is-paused', !playing);
    this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay);
  };

  Marquee.prototype.tweenTo = function (target, duration) {
    var self = this;
    this.hold();
    var from = this.pos;
    var start = performance.now();
    if (this.tweenFrame) window.cancelAnimationFrame(this.tweenFrame);
    var frame = function (now) {
      var k = duration ? Math.min(1, (now - start) / duration) : 1;
      self.pos = from + (target - from) * (1 - Math.pow(1 - k, 3));
      self.render();
      if (k < 1) self.tweenFrame = window.requestAnimationFrame(frame);
      else {
        self.tweenFrame = 0;
        self.run(self.pos);
      }
    };
    this.tweenFrame = window.requestAnimationFrame(frame);
  };

  // step to the previous / next image
  Marquee.prototype.step = function (direction) {
    var pos = this.offset();
    var widths = this.itemWidths || [];
    var start = 0;
    var index = 0;
    for (var i = 0; i < widths.length; i++) {
      if (start + widths[i] > pos + 1) { index = i; break; }
      start += widths[i];
    }
    var target = direction > 0 ? start + widths[index] : (pos - start > 1 ? start : start - widths[(index - 1 + widths.length) % widths.length]);
    this.tweenTo(target, reduceMotion.matches ? 0 : 400);
  };

  // the original media at the left edge of the strip
  Marquee.prototype.currentItem = function () {
    var pos = this.offset();
    var widths = this.itemWidths || [];
    var start = 0;
    for (var i = 0; i < widths.length; i++) {
      if (start + widths[i] / 2 > pos) return this.items[i];
      start += widths[i];
    }
    return this.items ? this.items[0] : null;
  };

  Marquee.prototype.enableNav = function () {
    this.gallery.querySelectorAll('.slider-button--prev, .slider-button--next').forEach(function (button) {
      button.removeAttribute('disabled');
    });
  };

  Marquee.prototype.bind = function () {
    var self = this;
    var gallery = this.gallery;
    var list = this.list;
    if (!list) return;

    // prev / next: move the strip instead of scrolling the (now clipped) list
    gallery.addEventListener('click', function (event) {
      if (!self.on) return;
      var nav = event.target.closest('.slider-button--prev, .slider-button--next');
      if (!nav || !gallery.contains(nav)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      self.step(nav.classList.contains('slider-button--next') ? 1 : -1);
    }, true);
    new MutationObserver(function () {
      if (self.on) self.enableNav();
    }).observe(gallery, { subtree: true, attributes: true, attributeFilter: ['disabled'] });

    if (this.toggle) {
      this.toggle.addEventListener('click', function () {
        self.setPlaying(!self.playing);
      });
    }
    var zoom = this.controls && this.controls.querySelector('.rd-marquee-controls__zoom');
    if (zoom) {
      zoom.addEventListener('click', function () {
        var item = self.on ? self.currentItem() : self.originals()[0];
        var opener = item && item.querySelector('.product__media-toggle');
        if (opener) openViewer(opener, zoom);
      });
    }

    // drag (mouse / pen / touch), the click that ends a drag does not open the viewer
    list.addEventListener('pointerdown', function (event) {
      if (!self.on || event.button !== 0) return;
      if (self.tweenFrame) window.cancelAnimationFrame(self.tweenFrame);
      self.tweenFrame = 0;
      self.dragging = true;
      self.moved = false;
      self.dragX = event.clientX;
      self.pointerId = event.pointerId;
    });
    window.addEventListener('pointermove', function (event) {
      if (!self.dragging || event.pointerId !== self.pointerId) return;
      var dx = event.clientX - self.dragX;
      if (!self.moved && Math.abs(dx) > 4) {
        self.moved = true;
        self.hold();
        self.dragPos = self.pos + dx;
        gallery.classList.add('rd-marquee--dragging');
      }
      if (self.moved) {
        self.pos = self.dragPos - dx;
        self.render();
      }
    });
    function endDrag(event) {
      if (!self.dragging || (event && event.pointerId !== self.pointerId)) return;
      self.dragging = false;
      if (!self.moved) return;
      gallery.classList.remove('rd-marquee--dragging');
      self.run(self.pos);
    }
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    // detail 0 = keyboard / element.click(): never the click that ends a drag (a drag released
    // outside the strip leaves the flag set, it must not swallow the next Enter or enlarge press)
    list.addEventListener('click', function (event) {
      if (!self.moved || event.detail === 0) return;
      self.moved = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    list.addEventListener('dragstart', function (event) {
      if (self.on) event.preventDefault();
    });

    // keyboard focus on an image: stop and show it (the clipped list must not scroll)
    list.addEventListener('focusin', function (event) {
      if (!self.on || self.dragging) return;
      var keyboard = true;
      try { keyboard = event.target.matches(':focus-visible'); } catch (e) { /* older browsers */ }
      if (!keyboard) return;
      var li = event.target.closest('.product__media-item');
      if (!li || li.hasAttribute('data-rd-clone')) return;
      self.setPlaying(false);
      self.viewer.scrollLeft = 0;
      list.scrollLeft = 0;
      var index = self.items ? self.items.indexOf(li) : -1;
      if (index < 0) return;
      var start = 0;
      for (var i = 0; i < index; i++) start += self.itemWidths[i];
      self.run(start);
    });

    // colour filter / variant media changes and resizes rebuild the loop
    var rebuild = debounce(function () { self.update(); }, 150);
    var isClone = function (node) {
      return node.nodeType !== 1 || node.hasAttribute('data-rd-clone');
    };
    new MutationObserver(function (records) {
      var relevant = records.some(function (r) {
        if (r.type === 'childList') {
          return Array.prototype.some.call(r.addedNodes, function (n) { return !isClone(n); }) ||
            Array.prototype.some.call(r.removedNodes, function (n) { return !isClone(n); });
        }
        // an original slide shown / hidden by the colour filter
        return r.target.parentElement === list && !isClone(r.target) && r.attributeName === 'class' &&
          (r.oldValue || '').indexOf('hidden') !== -1 !== r.target.classList.contains('hidden');
      });
      if (relevant) rebuild();
    }).observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'], attributeOldValue: true });
    window.addEventListener('resize', rebuild);
    desktop.addEventListener('change', function () { self.update(); });
    reduceMotion.addEventListener('change', function () { self.setPlaying(!reduceMotion.matches); });
    if (reduceMotion.matches) this.setPlaying(false);
  };

  // Strip without auto-scrolling (setting off): it is the theme's scrollable list; mouse / pen users
  // can drag it like the moving strip (touch keeps the native swipe), the click ending a drag does
  // not open the viewer.
  function DragScroll(gallery) {
    var list = gallery.querySelector('.product__media-list');
    if (!list) return;
    var down = false;
    var moved = false;
    var startX = 0;
    var startLeft = 0;
    var pointer = null;
    var active = function () {
      return desktop.matches && !gallery.classList.contains('rd-marquee--on') && !!gallery.closest('.product--columns, .product--stacked');
    };
    // the strip's photos sit side by side: load them all, so arrows and drags never show a blank slot
    var eager = function () {
      if (!active()) return;
      list.querySelectorAll(':scope > li:not(.hidden) .product__media img').forEach(function (img) {
        if (img.loading !== 'lazy') return;
        img.loading = 'eager';
        img.decoding = 'async';
      });
    };
    eager();
    desktop.addEventListener('change', eager);
    // a colour change shows other slides
    new MutationObserver(debounce(eager, 100)).observe(list, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    list.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'touch' || event.button !== 0 || !active()) return;
      down = true;
      moved = false;
      startX = event.clientX;
      startLeft = list.scrollLeft;
      pointer = event.pointerId;
    });
    window.addEventListener('pointermove', function (event) {
      if (!down || event.pointerId !== pointer) return;
      var dx = event.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        list.style.scrollSnapType = 'none';
        list.style.scrollBehavior = 'auto';
        gallery.classList.add('rd-strip--dragging');
      }
      if (moved) list.scrollLeft = startLeft - dx;
    });
    var end = function (event) {
      if (!down || (event && event.pointerId !== pointer)) return;
      down = false;
      if (!moved) return;
      gallery.classList.remove('rd-strip--dragging');
      list.style.scrollSnapType = '';
      list.style.scrollBehavior = '';
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    list.addEventListener('click', function (event) {
      if (!moved || event.detail === 0) return; // detail 0: keyboard / element.click(), see above
      moved = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    list.addEventListener('dragstart', function (event) {
      if (active()) event.preventDefault();
    });
  }

  function initMarquees(root) {
    // main product section only (the quick add popup re-uses the same markup)
    (root || document).querySelectorAll('section[id^="MainProduct-"] media-gallery').forEach(function (gallery) {
      if (gallery.hasAttribute('data-rd-marquee')) {
        if (!gallery.__rdMarquee) gallery.__rdMarquee = new Marquee(gallery);
      } else if (!gallery.__rdDrag) {
        gallery.__rdDrag = true;
        DragScroll(gallery);
      }
    });
  }

  /* ------------------------------------------------------------------ 2. drawers */
  function closeDialog(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    if (reduceMotion.matches) {
      dialog.close();
      return;
    }
    dialog.classList.add('is-closing');
    var done = function () {
      if (!dialog.classList.contains('is-closing')) return;
      dialog.classList.remove('is-closing');
      dialog.close();
    };
    dialog.addEventListener('animationend', done, { once: true });
    setTimeout(done, 450);
  }

  function openDialog(dialog) {
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;
    dialog.classList.remove('is-closing');
    dialog.showModal();
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    // product details row -> side drawer
    var opener = target.closest('.details-list-item[data-details-display="drawer"] > button.details-list-item__main');
    if (opener) {
      openDialog(opener.parentElement.querySelector('dialog.details-drawer'));
      return;
    }
    // accordion row
    var row = target.closest('.details-list-item[data-details-display="accordion"] > button.details-list-item__main');
    if (row) {
      var item = row.parentElement;
      if (!item.querySelector('.details-list-item__wrapper')) return;
      var open = !item.hasAttribute('open');
      item.toggleAttribute('open', open);
      row.setAttribute('aria-expanded', open);
      return;
    }
    // close buttons
    var close = target.closest('.details-drawer__close, .size-sheet__close');
    if (close) {
      closeDialog(close.closest('dialog'));
      return;
    }
    // click on the backdrop (outside the panel's box)
    if (target.matches('dialog.details-drawer, dialog.size-sheet') && target.open) {
      var r = target.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) {
        closeDialog(target);
      }
    }
  });

  // Esc: animated close
  document.addEventListener('cancel', function (event) {
    var dialog = event.target;
    if (dialog instanceof Element && dialog.matches('dialog.details-drawer, dialog.size-sheet')) {
      event.preventDefault();
      closeDialog(dialog);
    }
  }, true);

  document.addEventListener('keyup', function (event) {
    if (event.key !== 'Escape') return;
    var item = event.target instanceof Element && event.target.closest('.details-list-item[data-details-display="accordion"][open]');
    if (!item) return;
    var button = item.querySelector(':scope > button.details-list-item__main');
    item.removeAttribute('open');
    if (button) {
      button.setAttribute('aria-expanded', 'false');
      button.focus();
    }
  });

  /* ------------------------------------------------------------------ 4. sizes (helpers) */
  function variantsOf(selects) {
    if (!selects) return [];
    if (selects.__rdVariants) return selects.__rdVariants;
    var script = selects.querySelector('script[type="application/json"]');
    try {
      selects.__rdVariants = script ? JSON.parse(script.textContent) : [];
    } catch (e) {
      selects.__rdVariants = [];
    }
    return selects.__rdVariants;
  }

  function optionWrappers(selects) {
    return selects ? Array.prototype.slice.call(selects.querySelectorAll('.product-form__input')) : [];
  }

  function sizePosition(selects) {
    var wrappers = optionWrappers(selects);
    for (var i = 0; i < wrappers.length; i++) if (wrappers[i].hasAttribute('data-size-option')) return i + 1;
    return 0;
  }

  // values of the options as shown (null for an unchosen option)
  function optionValues(selects) {
    return optionWrappers(selects).map(function (wrapper) {
      var radio = wrapper.querySelector('input[type="radio"]:checked');
      if (radio) return radio.value;
      var select = wrapper.querySelector('select');
      return select ? select.value : null;
    });
  }

  // a variant with the given size and the other options as shown (an available one first)
  function variantFor(selects, position, value) {
    var variants = variantsOf(selects);
    var values = optionValues(selects);
    var matches = variants.filter(function (v) {
      return v.options.every(function (option, index) {
        if (index === position - 1) return value === null || option === value;
        return values[index] === null || values[index] === undefined || option === values[index];
      });
    });
    for (var i = 0; i < matches.length; i++) if (matches[i].available) return matches[i];
    return matches[0] || null;
  }

  function sizeRadios(selects) {
    return selects ? Array.prototype.slice.call(selects.querySelectorAll('[data-size-option] input[type="radio"]')) : [];
  }

  function sizeRadio(selects, value) {
    var found = null;
    sizeRadios(selects).forEach(function (radio) {
      if (radio.value === value) found = radio;
    });
    return found;
  }

  // while no size is chosen, no size radio is checked (screen readers, arrow keys)
  function uncheckSizes(selects) {
    if (!selects || !selects.hasAttribute('data-size-pending')) return;
    sizeRadios(selects).forEach(function (radio) { radio.checked = false; });
  }

  function clearPending(selects) {
    if (!selects || !selects.hasAttribute('data-size-pending')) return;
    selects.removeAttribute('data-size-pending');
    if (selects.hasAttribute('data-rd-url-lock')) {
      selects.removeAttribute('data-rd-url-lock');
      selects.removeAttribute('data-update-url');
    }
    var label = selects.querySelector('[data-size-option] [data-rd-label]');
    if (label) label.textContent = label.getAttribute('data-rd-label');
  }

  function prepareSelects(selects) {
    if (!selects || selects.__rdPrepared) return;
    selects.__rdPrepared = true;
    var inPopup = !!selects.closest('quick-add-modal');
    if (!selects.hasAttribute('data-size-pending')) return;
    if (inPopup) {
      // quick add popup: behave like the theme's quick add (the shown size is the one added)
      var idInput = scope(selects).querySelector('product-form input[name="id"]');
      var position = sizePosition(selects);
      var current = null;
      variantsOf(selects).forEach(function (v) { if (idInput && String(v.id) === idInput.value) current = v; });
      var radio = current && position ? sizeRadio(selects, current.options[position - 1]) : null;
      if (radio) radio.checked = true;
      selects.removeAttribute('data-size-pending');
      selects.removeAttribute('data-size-sheet-mobile');
      var label = selects.querySelector('[data-size-option] [data-rd-label]');
      if (label) label.textContent = label.getAttribute('data-rd-label');
      return;
    }
    // the colour can change while the size is not chosen: keep the URL clean (no size in ?variant=)
    if (selects.getAttribute('data-update-url') === 'false') selects.setAttribute('data-rd-url-lock', '');
    uncheckSizes(selects);
    // the size label shows only the option name while no size is chosen, also after the theme
    // re-renders it (e.g. a colour change hydrates "Taille: 35.5")
    var labelEl = selects.querySelector('[data-size-option] [data-rd-label]');
    var name = labelEl ? labelEl.textContent.trim() : null;
    new MutationObserver(function () {
      if (!selects.hasAttribute('data-size-pending') || !name) return;
      var current = selects.querySelector('[data-size-option] .form__label span');
      if (current && current.textContent.trim() !== name) {
        current.setAttribute('data-rd-label', current.textContent.trim());
        current.textContent = name;
      }
    }).observe(selects, { subtree: true, childList: true, characterData: true });
  }

  function initSelects(root) {
    (root || document).querySelectorAll('variant-selects').forEach(prepareSelects);
  }

  // before the theme handles a change: a size choice ends the "no size" state; another option
  // (colour) changing while no size is chosen gets a size checked for the theme's lookup only
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!(input instanceof Element)) return;
    var selects = input.closest('variant-selects');
    if (!selects) return;
    if (input.closest('[data-size-option]')) {
      clearPending(selects);
      return;
    }
    if (!selects.hasAttribute('data-size-pending')) return;
    var position = sizePosition(selects);
    var variant = position ? variantFor(selects, position, null) : null;
    var radio = variant ? sizeRadio(selects, variant.options[position - 1]) : null;
    if (!radio) return;
    radio.checked = true;
    setTimeout(function () { uncheckSizes(selects); }, 0);
  }, true);

  /* ------------------------------------------------------------------ 4. size sheet */
  function sheetFor(root) {
    if(root.closest && root.closest("quick-add-modal")) return null;
    if(root.querySelector("product-form moonbundle-component")) return null;
    var sheet = root.querySelector('dialog[data-size-sheet]');
    var selects = root.querySelector('variant-selects');
    if (!sheet || !selects) return null;
    var pending = selects.hasAttribute('data-size-pending');
    var onPhone = selects.hasAttribute('data-size-sheet-mobile') && phone.matches;
    return pending || onPhone ? sheet : null;
  }

  // mark availability / the chosen size on a list of size buttons ([data-value])
  function syncSizeButtons(buttons, selects, position, attribute) {
    var pending = selects.hasAttribute('data-size-pending');
    var chosen = optionValues(selects)[position - 1];
    buttons.forEach(function (button) {
      var variant = variantFor(selects, position, button.dataset.value);
      var available = !!(variant && variant.available && variant.options[position - 1] === button.dataset.value);
      button.disabled = !available;
      button.classList.toggle('is-unavailable', !available);
      var isChosen = !pending && chosen === button.dataset.value;
      if (attribute === 'aria-pressed') button.setAttribute('aria-pressed', isChosen ? 'true' : 'false');
      else setCurrent(button, isChosen);
    });
  }

  // add: once the size is chosen, add it to the basket (add to cart buttons); false: only choose it
  // (dynamic checkout: the shopper presses "Buy it now" again)
  function openSheet(sheet, root, add) {
    var selects = root.querySelector('variant-selects');
    syncSizeButtons(Array.prototype.slice.call(sheet.querySelectorAll('.size-sheet__size')), selects, Number(sheet.dataset.optionPosition), 'aria-current');
    sheet.__rdRoot = root;
    sheet.__rdAdd = add;
    openDialog(sheet);
  }

  function waitFor(test, timeout) {
    return new Promise(function (resolve) {
      var start = Date.now();
      (function check() {
        if (test() || Date.now() - start > timeout) resolve();
        else setTimeout(check, 50);
      })();
    });
  }

  // choose a size (sheet / size guide) on the page, then (add) add it to the basket
  function chooseSize(root, value, add) {
    var selects = root.querySelector('variant-selects');
    var position = sizePosition(selects);
    var target = position ? variantFor(selects, position, value) : null;
    var radio = sizeRadio(selects, value);
    if (radio && !radio.checked) radio.click();
    else clearPending(selects);
    if (!add) return Promise.resolve();
    var idInput = root.querySelector('product-form input[name="id"]');
    return waitFor(function () {
      var atc = root.querySelector('product-form .product-form__submit');
      return (!target || !idInput || Number(idInput.value) === target.id) && atc && !atc.disabled && !atc.classList.contains('loading');
    }, 5000).then(function () {
      var atc = root.querySelector('product-form .product-form__submit');
      if (!atc || atc.disabled) return;
      root.__rdSizeConfirmed = true;
      atc.click();
      root.__rdSizeConfirmed = false;
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target instanceof Element && event.target.closest('dialog[data-size-sheet] .size-sheet__size');
    if (!button || button.disabled) return;
    var sheet = button.closest('dialog');
    var root = sheet.__rdRoot || scope(sheet);
    closeDialog(sheet);
    chooseSize(root, button.dataset.value, sheet.__rdAdd !== false);
  });

  // "choose a size" message (red error toast): tablets / desktop show the size row on the page,
  // so Add to cart without a size points to it instead of opening the size panel. One message at a
  // time: a new press restarts its time on screen (~5.6s) and has it read out again. Kept on <body>
  // (fixed, never inside a blurred / transformed panel), created before its first message so that
  // its alert role is announced.
  var sizeToast = null;
  var sizeToastTimer = 0;
  var sizeToastClear = 0;
  var TOAST_ICON = '<svg class="rd-size-toast__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
    '<circle cx="10" cy="10" r="8.25" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="m7 7 6 6m0-6-6 6" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';

  function prepareSizeToast() {
    if (sizeToast || !document.querySelector('dialog[data-size-sheet][data-size-required]')) return;
    sizeToast = document.createElement('div');
    sizeToast.className = 'rd-size-toast';
    sizeToast.setAttribute('role', 'alert');
    document.body.appendChild(sizeToast);
  }

  function hideSizeToast() {
    if (!sizeToast) return;
    sizeToast.classList.remove('is-visible');
    sizeToastClear = window.setTimeout(function () {
      if (!sizeToast.classList.contains('is-visible')) sizeToast.textContent = '';
    }, 250);
  }

  function showSizeToast(text) {
    if (!text) return;
    prepareSizeToast();
    if (!sizeToast) return;
    window.clearTimeout(sizeToastTimer);
    window.clearTimeout(sizeToastClear);
    // new nodes each time (same frame: no flicker), so the alert is read out again
    sizeToast.innerHTML = TOAST_ICON + '<span class="rd-size-toast__text"></span>';
    sizeToast.lastChild.textContent = text;
    if (!sizeToast.classList.contains('is-visible')) {
      void sizeToast.offsetWidth; // start the entry from the hidden state
      sizeToast.classList.add('is-visible');
    }
    sizeToastTimer = window.setTimeout(hideSizeToast, 5600);
  }

  // add to cart buttons (page, sticky bar) ask for the size first; so does "Buy it now" (dynamic
  // checkout, caught on its wrapper: the button itself can live in a shadow root or an iframe)
  // while no size is chosen: phones open the size sheet, larger screens show the message above
  document.addEventListener('click', function (event) {
    var button = event.target instanceof Element && event.target.closest('.product-form__submit, .sticky-atc button.main-product-atc, .shopify-payment-button');
    if (!button || button.disabled) return;
    var root = scope(button);
    if (root.__rdSizeConfirmed) return;
    var payment = button.classList.contains('shopify-payment-button');
    if (payment && !root.querySelector('variant-selects[data-size-pending]')) return;
    var sheet = sheetFor(root);
    if (!sheet) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!phone.matches && sheet.hasAttribute('data-size-required')) {
      showSizeToast(sheet.getAttribute('data-size-required'));
      return;
    }
    openSheet(sheet, root, !payment);
  }, true);

  /* ------------------------------------------------------------------ 3. size guide */
  var sizeGuideReturn = null;

  function openSizeGuide() {
    return document.querySelector('modal-v2.sizing-chart[data-open="true"]');
  }

  function guideRoot(modal) {
    return (modal.closest('quick-add-modal') && modal.closest('quick-add-modal').querySelector('product-info')) || mainRoot();
  }

  function prepareGuide(modal) {
    var body = modal.querySelector('.sizing-chart__body');
    if (body) body.scrollTop = 0;
    modal.querySelector('.sizing-chart__modal').scrollTop = 0;
    var row = modal.querySelector('[data-size-guide]');
    var root = guideRoot(modal);
    var selects = root && root.querySelector('variant-selects');
    if (row && selects) {
      syncSizeButtons(Array.prototype.slice.call(row.querySelectorAll('.sizing-chart__size')), selects, Number(row.dataset.optionPosition), 'aria-pressed');
    }
    setTimeout(function () {
      var title = modal.querySelector('.sizing-chart__title');
      if (title) title.focus({ preventScroll: true });
    }, 60);
  }

  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // page trigger "Size guide"
    var trigger = target.closest('.sizing-chart__button');
    if (trigger) {
      sizeGuideReturn = trigger;
      setTimeout(function () {
        var modal = openSizeGuide();
        if (modal) prepareGuide(modal);
      }, 30);
      return;
    }

    // "Size guide" in the size sheet (phones: the size row is not on the page)
    var sheetGuide = target.closest('.size-sheet__guide');
    if (sheetGuide) {
      var sheet = sheetGuide.closest('dialog');
      var root = sheet.__rdRoot || scope(sheet);
      sizeGuideReturn = root.querySelector('product-form .product-form__submit');
      // closed at once (the guide slides in over it), so the browser hands focus back before
      // the guide takes it
      sheet.classList.remove('is-closing');
      if (sheet.open) sheet.close();
      var modal = document.querySelector('.modals-manager > modal-v2.sizing-chart') || document.querySelector('modal-v2.sizing-chart');
      if (modal && typeof modal.openModal === 'function') {
        modal.openModal();
        prepareGuide(modal);
      }
      return;
    }

    // a size in the size guide: choose it on the page
    var size = target.closest('modal-v2.sizing-chart .sizing-chart__size');
    if (size && !size.disabled) {
      var guide = size.closest('modal-v2');
      var guideProduct = guideRoot(guide);
      var selects = guideProduct.querySelector('variant-selects');
      var radio = sizeRadio(selects, size.dataset.value);
      if (radio && !radio.checked) radio.click();
      else clearPending(selects);
      guide.querySelectorAll('.sizing-chart__size').forEach(function (b) {
        b.setAttribute('aria-pressed', b === size ? 'true' : 'false');
      });
      return;
    }

    // add to cart in the size guide
    var guideAtc = target.closest('modal-v2.sizing-chart [data-size-guide-atc]');
    if (guideAtc) {
      var guideModal = guideAtc.closest('modal-v2');
      var product = guideRoot(guideModal);
      var productSelects = product.querySelector('variant-selects');
      if (productSelects && productSelects.hasAttribute('data-size-pending')) {
        var first = guideModal.querySelector('.sizing-chart__size:not(:disabled)');
        if (first) first.focus();
        return;
      }
      var pressed = guideModal.querySelector('.sizing-chart__size[aria-pressed="true"]');
      var closeButton = guideModal.querySelector('.popup-modal__close');
      if (closeButton) closeButton.click();
      var value = pressed ? pressed.dataset.value : optionValues(productSelects)[sizePosition(productSelects) - 1];
      chooseSize(product, value, true);
    }
  });

  document.addEventListener('keydown', function (event) {
    var modal = openSizeGuide();
    if (!modal) return;
    if (event.key === 'Escape') {
      var close = modal.querySelector('.popup-modal__close');
      if (close) close.click();
      return;
    }
    if (event.key !== 'Tab') return;
    var panel = modal.querySelector('.sizing-chart__modal');
    var focusables = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), isShown);
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (!panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  function watchSizeGuides(root) {
    (root || document).querySelectorAll('modal-v2.sizing-chart').forEach(function (modal) {
      if (modal.__rdWatched) return;
      modal.__rdWatched = true;
      new MutationObserver(function () {
        if (modal.getAttribute('data-open') === 'true') return;
        var back = sizeGuideReturn;
        sizeGuideReturn = null;
        if (back && isShown(back)) back.focus({ preventScroll: true });
        else {
          var product = mainRoot();
          var atc = product && product.querySelector('product-form .product-form__submit');
          if (back && atc) atc.focus({ preventScroll: true });
        }
      }).observe(modal, { attributes: true, attributeFilter: ['data-open'] });
    });
  }

  /* ------------------------------------------------------------------ 5. phones */
  // floating add to cart bar: shown while the main button is not fully visible under the header
  function watchStickyBar(root) {
    (root || document).querySelectorAll('section[id^="MainProduct-"]').forEach(function (section) {
      if (section.__rdSticky || !window.IntersectionObserver) return;
      var bar = section.querySelector('sticky-atc');
      var atc = section.querySelector('product-form .product-form__submit');
      if (!bar || !atc) return;
      section.__rdSticky = true;
      var header = document.querySelector('.section-header, sticky-header');
      var top = header ? Math.round(header.getBoundingClientRect().height) : 0;
      new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        bar.classList.toggle('rd-sticky-visible', entry.intersectionRatio < 1 && phone.matches);
      }, { threshold: [0, 1], rootMargin: '-' + top + 'px 0px 0px 0px' }).observe(atc);
    });
  }

  /* ------------------------------------------------------------------ 6. image viewer */
  // opens the viewer with a strip photo's own opener, then makes the control that was pressed the
  // one the viewer gives the focus back to (the theme focused the strip photo: the page jumped up)
  function openViewer(toggle, opener) {
    toggle.click();
    var host = toggle.closest('modal-opener');
    var modal = host && document.querySelector(host.getAttribute('data-modal'));
    if (!modal || !modal.hasAttribute('open') || modal.openedBy !== toggle) return;
    modal.__rdMedia = toggle.getAttribute('data-media-id');
    modal.openedBy = opener;
  }

  document.addEventListener('click', function (event) {
    var button = event.target instanceof Element && event.target.closest('.product__secondary-media[data-media-id]');
    if (!button) return;
    var section = button.closest('section') || document;
    var toggle = section.querySelector('.product__media-list > li:not([data-rd-clone]) .product__media-toggle[data-media-id="' + button.dataset.mediaId + '"]');
    if (toggle) openViewer(toggle, button);
  });

  function galleryHides(id) {
    var slide = document.querySelector('section[id^="MainProduct-"] .product__media-list > li[data-media-id$="-' + id + '"]:not([data-rd-clone])');
    return !!(slide && slide.classList.contains('hidden'));
  }

  // thumbnail rail of the viewer, rebuilt at each opening (the colour filter can change the set)
  function buildRail(modal) {
    var content = modal.querySelector('.product-media-modal__content');
    var dialog = modal.querySelector('.product-media-modal__dialog');
    if (!content || !dialog) return;
    var old = dialog.querySelector('.rd-viewer-rail');
    if (old) old.remove();
    if (modal.__rdRailObserver) modal.__rdRailObserver.disconnect();
    var media = Array.prototype.filter.call(content.querySelectorAll(':scope > [data-media-id]'), function (item) {
      return !galleryHides(item.getAttribute('data-media-id'));
    });
    if (media.length < 2) return;
    var rail = document.createElement('div');
    rail.className = 'rd-viewer-rail';
    var template = modal.getAttribute('data-thumb-label') || '[index] / [count]';
    var thumbs = [];
    var mark = function (id) {
      thumbs.forEach(function (thumb) { setCurrent(thumb, thumb.getAttribute('data-target') === id); });
    };
    media.forEach(function (item, index) {
      var img = item.tagName === 'IMG' ? item : item.querySelector('img');
      if (!img) return;
      var id = item.getAttribute('data-media-id');
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'rd-viewer-rail__thumb';
      button.setAttribute('data-target', id);
      button.setAttribute('aria-label', template.replace('[index]', index + 1).replace('[count]', media.length));
      var thumb = document.createElement('img');
      thumb.src = (img.getAttribute('src') || '').replace(/([?&]width=)\d+/, '$1160');
      thumb.alt = '';
      thumb.width = 48;
      var w = Number(img.getAttribute('width')) || img.naturalWidth;
      var h = Number(img.getAttribute('height')) || img.naturalHeight;
      if (w && h) thumb.height = Math.round((48 * h) / w);
      button.appendChild(thumb);
      button.addEventListener('click', function () {
        mark(id);
        item.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      });
      thumbs.push(button);
      rail.appendChild(button);
    });
    // the theme closes the viewer on any mouse release outside a video: not on the rail
    ['pointerup', 'click'].forEach(function (type) {
      rail.addEventListener(type, function (event) { event.stopPropagation(); });
    });
    // before the photos: reachable with Tab between the close button and the photos (the theme's
    // focus trap only wraps around its first and last elements)
    if (content.parentNode === dialog) dialog.insertBefore(rail, content);
    else dialog.appendChild(rail);
    // opened from the enlarge button: the photo is the one openViewer() noted
    var opened = (modal.openedBy && modal.openedBy.getAttribute('data-media-id')) || modal.__rdMedia;
    mark(opened || media[0].getAttribute('data-media-id'));
    if (window.IntersectionObserver) {
      modal.__rdRailObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) mark(entry.target.getAttribute('data-media-id'));
        });
      }, { root: content, rootMargin: '-50% 0px -50% 0px', threshold: 0 });
      media.forEach(function (item) { modal.__rdRailObserver.observe(item); });
    }
  }

  function watchViewers(root) {
    (root || document).querySelectorAll('product-modal').forEach(function (modal) {
      if (modal.__rdWatched) return;
      modal.__rdWatched = true;
      new MutationObserver(function () {
        if (modal.hasAttribute('open')) buildRail(modal);
      }).observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
  }

  /* ------------------------------------------------------------------ 7. dots */
  function watchDots(root) {
    (root || document).querySelectorAll('media-gallery .slider__dots').forEach(function (dots) {
      if (dots.__rdWatched) return;
      dots.__rdWatched = true;
      var template = dots.getAttribute('data-label-template');
      var sync = function () {
        var links = Array.prototype.slice.call(dots.querySelectorAll('.slider-counter__link'));
        var shown = links.filter(function (link) { return !link.classList.contains('hidden'); });
        links.forEach(function (link) {
          setCurrent(link, link.classList.contains('slider-counter__link--active'));
          var index = shown.indexOf(link);
          if (template && index > -1) {
            var label = template.replace('[index]', index + 1).replace('[count]', shown.length);
            if (link.getAttribute('aria-label') !== label) link.setAttribute('aria-label', label);
          }
        });
      };
      sync();
      new MutationObserver(sync).observe(dots, { subtree: true, attributes: true, attributeFilter: ['class'] });
    });
  }

  /* ------------------------------------------------------------------ quick add popups */
  // the popup content is replaced at each opening: prepare its variant picker every time
  function watchPopups() {
    document.querySelectorAll('quick-add-modal').forEach(function (popup) {
      if (popup.__rdWatched) return;
      popup.__rdWatched = true;
      new MutationObserver(function () { initSelects(popup); }).observe(popup, { childList: true, subtree: true });
    });
  }

  function init(root) {
    initSelects(root);
    initMarquees(root);
    watchSizeGuides(root);
    watchStickyBar(root);
    watchViewers(root);
    watchDots(root);
    watchPopups();
    prepareSizeToast();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
  // theme editor / late sections
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  new MutationObserver(debounce(function () { init(); }, 300)).observe(document.body, { childList: true, subtree: false });
})();
